<template>
  <div>
    <BaseTitle class="mb-6" v-text="$tc('organization.folder', 1)" />
    <Board>
      <template #header>
        <div class="header">
          <div class="header_left">
            <FolderCreateAndEditDialog />
          </div>
          <div class="header_center">
            <BaseButton
              v-if="selectedFolderToArchive.length"
              :loading="isArchiving"
              type="primary"
              class="ml-3"
              @click.stop="archiveFolders"
            >
              <BaseIcon class="icon" icon="$mdiArchive" small left />
              <span
                v-text="
                  `${$t('utils.archive')} (${selectedFolderToArchive.length})`
                "
              />
            </BaseButton>
            <BaseButton
              v-if="selectedFolderToUnArchive.length"
              :loading="isUnArchiving"
              type="primary"
              class="ml-3"
              @click.stop="unArchiveFolders"
            >
              <BaseIcon class="icon" icon="$mdiCheck" small left />
              <span
                v-text="
                  `${$t('utils.unarchive')} (${
                    selectedFolderToUnArchive.length
                  })`
                "
              />
            </BaseButton>

            <div v-if="activeFilters.length" class="filters">
              <div class="filter_icon">
                <BaseIcon icon="$mdiFilterOutline" />
              </div>
              <div
                v-for="(filter, i) in activeFilters"
                :key="`f-${i}`"
                class="filter_item"
              >
                <div v-if="filter.prependIcon" class="filter_item_icon">
                  <BaseIcon :icon="filter.prependIcon" />
                </div>
                <div class="filter_item_label">
                  {{ filter.label }}
                </div>
                <div class="close_icon" @click="removeFilter(filter)">
                  <BaseIcon icon="$mdiClose" />
                </div>
              </div>
            </div>
          </div>
          <div class="header_right">
            <v-text-field
              v-model="queryString"
              outlined
              :label="$t('folder.search')"
              prepend-inner-icon="$mdiMagnify"
              dense
              hide-details
              @input="searchFolderWithQueryString()"
            />
          </div>
        </div>
        <div class="filter-container">
          <div
            class="filter-title"
            @click.stop="isFilterSectionOpen = !isFilterSectionOpen"
          >
            <BaseIcon
              :icon="isFilterSectionOpen ? '$mdiMenuUp' : '$mdiMenuDown'"
              color="primary"
            />
            <span class="filter">{{ $t("utils.filters") }}</span>
            <div class="separator" />
          </div>
          <template v-if="isFilterSectionOpen">
            <div class="filter-items">
              <div
                v-for="filter in advancedFilters"
                :key="filter.key"
                class="filter-item"
              >
                <div class="filter-label-text">{{ `${filter.label} :` }}</div>
                <template>
                  <base-autocomplete
                    v-model="filter.value"
                    :loading="filter.items.loading"
                    :items="
                      filter.key === 'status'
                        ? statusName
                        : filter.items.results
                    "
                    :filter="() => true"
                    :search-input.sync="filter.items.queryString"
                    :item-text="filter.component.text"
                    :item-value="filter.component.value"
                    clearable
                    :placeholder="filter.label"
                    :multiple="filter.component.multiple"
                    class="input-field"
                    @change="applyFilters(filter)"
                  >
                    <template #append-item>
                      <div
                        v-if="filter.key === 'client'"
                        v-intersect="loadMoreClients"
                        loadMoreCollaborators
                      />
                      <div
                        v-if="filter.key === 'assign'"
                        v-intersect="loadMoreCollaborators"
                      />
                    </template>
                  </base-autocomplete>
                </template>
              </div>
            </div>
            <div class="separator" />
          </template>
        </div>
      </template>
      <template #main>
        <v-tabs v-model="activeTab" class="tab_header">
          <v-tab class="tab_header_item" @click="changeTab('OPEN')">
            <BaseIcon icon="$mdiCheck" small class="pr-3" />
            <span>{{ folderActiveTabLabel }}</span>
          </v-tab>
          <v-tab class="tab_header_item" @click="changeTab('CLOSED')">
            <BaseIcon icon="$mdiArchive" small class="pr-3" />
            <span>{{ folderArchivedTabLabel }}</span>
          </v-tab>
        </v-tabs>
        <v-tabs-items v-model="activeTab" class="tab_items">
          <v-tab-item>
            <FolderListTable
              v-model="filters"
              :folder-detail="activeFolder.detail"
              :loading="activeFolder.loading"
              :organization-id="organizationId"
              need-contact
              @select-folders="(item) => (selectedFolder = item)"
              @inputValue="handleInputFilter()"
              @changeValue="handleChangeFilter('OPEN')"
            />
          </v-tab-item>
          <v-tab-item>
            <FolderListTable
              v-model="filters"
              :folder-detail="archivedFolder.detail"
              :loading="archivedFolder.loading"
              :organization-id="organizationId"
              need-contact
              is-archived
              @select-folders="(item) => (selectedFolder = item)"
              @inputValue="handleInputFilter()"
              @changeValue="handleChangeFilter('CLOSED')"
            />
          </v-tab-item>
        </v-tabs-items>
      </template>
    </Board>
  </div>
</template>
<script>
import Board from "@/core/Components/Board";
import FolderCreateAndEditDialog from "../Components/FolderCreateAndEditDialog";
import FolderListTable from "../Components/FolderListTable";
import {
  findOrganizationFolders,
  postArchiveFolders,
  postUnArchiveFolders,
} from "../Services/folder.service";
import Folder from "@/core/Models/Folder";
import { mapState } from "vuex";
import ClientAPI from "@/modules/Client/Services/client.api";
import OrganizationAPI from "@/core/Services/organization.api";
export default {
  name: "FolderList",
  components: {
    Board,
    FolderCreateAndEditDialog,
    FolderListTable,
  },
  props: {
    organizationId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      activeTab: 0,
      selectedFolder: [],
      isArchiving: false,
      isUnArchiving: false,
      isFilterSectionOpen: false,
      advancedFilters: [
        {
          key: "client",
          label: this.$tc("utils.contact", 1),
          value: [],
          multiple: true,
          component: {
            multiple: true,
            text: "email",
            value: "email",
          },
          items: {
            results: [],
            loading: false,
            page: 1,
            pageSize: 50,
            queryString: "",
            searchTimeoutRef: undefined,
            next: null,
          },
        },
        {
          key: "workflow",
          label: this.$t("utils.workflow"),
          value: [],
          component: {
            multiple: true,
            text: "name",
            value: "name",
          },
          items: {
            results: [],
            loading: false,
            page: 1,
            pageSize: 100,
            queryString: "",
            searchTimeoutRef: undefined,
            next: null,
          },
        },
        {
          key: "status",
          label: this.$tc("utils.status", 2),
          value: [],
          component: {
            multiple: true,
            text: "name",
            value: "name",
          },
          items: {
            results: [],
            loading: false,
            page: 1,
            pageSize: 10,
            queryString: "",
            searchTimeoutRef: undefined,
            next: null,
          },
        },
        {
          key: "assign",
          label: this.$tc("organization.collaborator", 2),
          value: null,
          component: {
            multiple: true,
            text: "user.email",
            value: "user.email",
          },
          items: {
            results: [],
            loading: false,
            page: 1,
            pageSize: 100,
            queryString: "",
            searchTimeoutRef: undefined,
            next: null,
          },
        },
      ],
      activeFolder: {
        loading: true,
        detail: {
          count: 0,
          next: 0,
          previous: 0,
          folders: [],
        },
      },
      filters: {
        pageSize: 10,
        page: 1,
        ordering: "",
        name: "",
        activeFolderPage: 1,
        archivedFolderPage: 1,
        status: [],
        assign: [],
        workflow: [],
        client: [],
      },
      archivedFolder: {
        loading: true,
        detail: {
          count: 0,
          next: 0,
          previous: 0,
          folders: [],
        },
      },
      queryString: "",
      searchTimeoutRef: null,
    };
  },
  computed: {
    ...mapState({
      selectedFolderToArchive: (state) => state.folder.selectedFolderToArchive,
      selectedFolderToUnArchive: (state) =>
        state.folder.selectedFolderToUnArchive,
    }),
    contactFilter() {
      return this.advancedFilters.find((item) => item.key === "client");
    },
    collaboratorFilter() {
      return this.advancedFilters.find((item) => item.key === "assign");
    },
    workflowFilter() {
      return this.advancedFilters.find((item) => item.key === "workflow");
    },
    statusFilter() {
      return this.advancedFilters.find((item) => item.key === "status");
    },
    statusName() {
      if (!this.workflowFilter?.items.results.length) return [];
      let tmp = [];
      this.workflowFilter?.items.results
        .filter((w) => {
          if (!this.workflowFilter.value.length) return w;
          return this.workflowFilter.value.includes(w.name);
        })
        .forEach((workflow) => {
          workflow.statuses.forEach((status) => tmp.push(status.name));
        });
      return tmp;
    },
    folderActiveTabLabel() {
      const count = this.activeFolder.detail.count;
      return `${this.$tc("utils.activeFolder", count ? count : 1)} (${count})`;
    },
    folderArchivedTabLabel() {
      const count = this.archivedFolder.detail.count;
      return `${this.$tc(
        "utils.archivedFolders",
        count ? count : 1
      )} (${count})`;
    },

    activeFilters() {
      const filters = [];
      if (this.filters.ordering) {
        const orderFilter = {
          prependIcon: this.filters.ordering.startsWith("-")
            ? "$mdiArrowDown"
            : "$mdiArrowUp",
          label: "",
          key: "ordering",
        };
        switch (this.filters.ordering) {
          case "name":
          case "-name":
            orderFilter.label = "Dossier";
            break;
          case "client__email":
          case "-client__email":
            orderFilter.label = "Contact";
            break;
          case "workflow__name":
          case "-workflow__name":
            orderFilter.label = "Workflow";
            break;
          case "status__name":
          case "-status__name":
            orderFilter.label = "Status";
            break;
          case "assign__email":
          case "-assign__email":
            orderFilter.label = "Collaborateur";
            break;
          case "date_updated":
          case "-date_updated":
            orderFilter.label = "Date de modification";
            break;
        }
        filters.push(orderFilter);
      }
      if (this.filters.name) {
        filters.push({
          label: `Dossier : ${this.filters.name}`,
          key: "name",
        });
      }
      if (this.filters.client?.length) {
        filters.push({
          label: `${this.filters.client?.length} Contact${
            this.filters.client?.length > 1 ? "s" : ""
          } : ${this.filters.client[0]}${
            this.filters.client.length > 1 ? ".." : ""
          }`,
          key: "client",
        });
      }
      if (this.filters.workflow?.length) {
        filters.push({
          label: `${this.filters.workflow?.length} Workflow${
            this.filters.workflow?.length > 1 ? "s" : ""
          } : ${this.filters.workflow[0]}${
            this.filters.workflow.length > 1 ? ".." : ""
          }`,
          key: "workflow",
        });
      }
      if (this.filters.status?.length) {
        filters.push({
          label: `${this.filters.status?.length} Statut${
            this.filters.status?.length > 1 ? "s" : ""
          } : ${this.filters.status[0]}${
            this.filters.status.length > 1 ? ".." : ""
          }`,
          key: "status",
        });
      }
      if (this.filters.assign?.length) {
        filters.push({
          label: `${this.filters.assign?.length} Collaborateur${
            this.filters.assign?.length > 1 ? "s" : ""
          } : ${this.filters.assign[0]}${
            this.filters.assign.length > 1 ? ".." : ""
          }`,
          key: "assign",
        });
      }
      return filters;
    },
  },
  watch: {
    "contactFilter.items.queryString": {
      handler() {
        this.handleClientSearch();
      },
    },
    "collaboratorFilter.items.queryString": {
      handler() {
        this.handleCollaboratorSearch();
      },
    },
  },
  created() {
    try {
      this.initialize();
      this.initFiltersContent();
    } catch {
      this.$store.dispatch("snackbar/active", {
        message: this.$t("utils.errorHasHappen"),
        type: "ERROR",
      });
    }
  },
  destroyed() {
    this.$store.commit("folder/setSelectedFolderToArchive", []);
    this.$store.commit("folder/setSelectedFolderToUnArchive", []);
  },
  methods: {
    // client
    async applyFilters(filter) {
      this.filters[filter.key] = filter.value;
      await this.fetchFolders();
    },
    async fetchClients() {
      if (
        this.contactFilter.items.loading &&
        !this.contactFilter.items.page &&
        !this.organizationId
      )
        return;
      this.contactFilter.items.loading = true;
      const res = await ClientAPI.getOrganizationClients(
        this.organizationId,
        this.contactFilter.items.page,
        this.contactFilter.items.pageSize,
        this.contactFilter.items.queryString
      );
      this.contactFilter.items = {
        ...this.contactFilter.items,
        results: [...this.contactFilter.items.results, ...res.data.results],
        loading: false,
        next: res.data.next,
      };
    },
    async loadMoreClients(_, __, isIntersecting) {
      if (
        !isIntersecting ||
        this.contactFilter.items.loading ||
        !this.contactFilter.items.next
      )
        return;
      try {
        this.contactFilter.items.page = this.contactFilter.items.next;
        await this.fetchClients();
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
    },
    handleClientSearch() {
      this.contactFilter.items.results = [];
      this.contactFilter.items.page = 1;
      if (this.contactFilter.items.searchTimeoutRef)
        clearTimeout(this.contactFilter.items.searchTimeoutRef);
      this.contactFilter.items.searchTimeoutRef = setTimeout(async () => {
        this.fetchClients();
      }, 500);
    },

    // colaborator
    async fetchCollaborators() {
      if (
        this.collaboratorFilter.items.loading &&
        !this.collaboratorFilter.items.page &&
        !this.organizationId
      )
        return;
      this.collaboratorFilter.items.loading = true;
      const res = await OrganizationAPI.getOrganizationCollaborators(
        this.organizationId,
        this.collaboratorFilter.items.page,
        this.collaboratorFilter.items.queryString
      );
      this.collaboratorFilter.items = {
        ...this.collaboratorFilter.items,
        results: [
          ...this.collaboratorFilter.items.results,
          ...res.data.results,
        ],
        next: res.data.next,
      };
      this.collaboratorFilter.items.loading = false;
    },
    handleCollaboratorSearch() {
      this.collaboratorFilter.items.results = [];
      this.collaboratorFilter.items.page = 1;
      if (this.collaboratorFilter.items.searchTimeoutRef)
        clearTimeout(this.collaboratorFilter.items.searchTimeoutRef);
      this.collaboratorFilter.items.searchTimeoutRef = setTimeout(async () => {
        this.fetchCollaborators();
      }, 500);
    },

    async loadMoreCollaborators(_, __, isIntersecting) {
      if (
        !isIntersecting ||
        this.collaboratorFilter.items.loading ||
        !this.collaboratorFilter.items.next
      )
        return;
      try {
        this.collaboratorFilter.items.page = this.collaboratorFilter.items.next;
        await this.fetchCollaborators();
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
    },
    // workflow
    async fetchWorkflows() {
      this.workflowFilter.items.results = await this.$store.dispatch(
        "workflow/fetchWorkflows",
        this.$route.params.organizationId
      );
    },
    async archiveFolders() {
      this.isArchiving = true;
      try {
        const folderPayload = new FormData();
        this.selectedFolderToArchive.forEach((folder) => {
          return folderPayload.append("folders", folder);
        });
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.snackbar.archiveMightTakeTime"),
          type: "LOADING",
        });
        await postArchiveFolders({
          organizationId: this.organizationId,
          payload: folderPayload,
        });

        await this.fetchFolders();
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.snackbar.folderArchivedSuccessfully"),
          type: "SUCCESS",
        });
        this.$store.commit("folder/setSelectedFolderToArchive", []);
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.isArchiving = false;
    },
    async unArchiveFolders() {
      this.isUnArchiving = true;
      try {
        const folderPayload = new FormData();
        this.selectedFolderToUnArchive.forEach((folder) => {
          return folderPayload.append("folders", folder);
        });
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.snackbar.archiveMightTakeTime"),
          type: "LOADING",
        });
        await postUnArchiveFolders({
          organizationId: this.organizationId,
          payload: folderPayload,
        });

        await this.fetchFolders();
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.snackbar.folderUnarchivedSuccessfully"),
          type: "SUCCESS",
        });
        this.$store.commit("folder/setSelectedFolderToUnArchive", []);
      } catch {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
      this.isUnArchiving = false;
    },
    async initFiltersContent() {
      Promise.all[
        (this.fetchClients(), this.fetchCollaborators(), this.fetchWorkflows())
      ];
    },
    async initialize() {
      if (this.$route.query.status && this.$route.query.workflow)
        this.initFiltersByRouteQuery();
      else this.initFilters(this.organizationId);

      this.fetchFolders();
    },
    initFiltersByRouteQuery() {
      if (this.$route.query.status && this.$route.query.workflow) {
        this.filters.status.push(this.$route.query.status);
        this.filters.workflow.push(this.$route.query.workflow);
      }
    },
    initFilters(organizationId) {
      let organizationsFilters = localStorage.getItem("organizationsFilters");
      if (!organizationsFilters || !organizationsFilters.length) return;
      organizationsFilters = JSON.parse(organizationsFilters);
      const tmpFilters = organizationsFilters.find(
        (of) => of.organizationId == organizationId
      );
      if (!tmpFilters?.filters) return;
      this.filters = tmpFilters.filters;
      for (const [key, value] of Object.entries(tmpFilters.filters)) {
        const tmp = this.advancedFilters.find((item) => item.key === key);
        if (tmp) tmp.value = value;
      }
    },
    saveFilters(organizationId) {
      let organizationsFilters = localStorage.getItem("organizationsFilters");
      const newFilters = {
        organizationId: organizationId,
        filters: this.filters,
      };
      if (!organizationsFilters || !organizationsFilters.length)
        organizationsFilters = [newFilters];
      else {
        organizationsFilters = JSON.parse(organizationsFilters);
        if (
          organizationsFilters.some((of) => of.organizationId == organizationId)
        )
          organizationsFilters = organizationsFilters.map((of) =>
            of.organizationId == organizationId ? newFilters : of
          );
        else organizationsFilters.push(newFilters);
      }
      localStorage.setItem(
        "organizationsFilters",
        JSON.stringify(organizationsFilters)
      );
    },
    async fetchFolders(saveFilter = true) {
      try {
        if (saveFilter) this.saveFilters(this.organizationId);
        await Promise.all([
          this.fetchActiveFolders(),
          this.fetchArchivedFolders(),
        ]);
      } catch (error) {
        this.$store.dispatch("snackbar/active", {
          message: this.$t("utils.errorHasHappen"),
          type: "ERROR",
        });
      }
    },
    async fetchActiveFolders() {
      this.activeFolder.loading = true;
      const res = await findOrganizationFolders({
        organizationId: this.organizationId,
        filters: {
          ...this.filters,
          page: this.filters.activeFolderPage
            ? this.filters.activeFolderPage
            : 1,
          state: "OPEN",
        },
      });
      this.activeFolder.detail = {
        ...res.data,
        folders: res.data.results.map((f) => {
          const res = new Folder(f);
          return res;
        }),
      };
      this.activeFolder.loading = false;
    },
    async fetchArchivedFolders() {
      this.archivedFolder.loading = true;
      const res = await findOrganizationFolders({
        organizationId: this.organizationId,
        filters: {
          ...this.filters,
          page: this.filters.archivedFolderPage
            ? this.filters.archivedFolderPage
            : 1,
          state: "CLOSED",
        },
      });
      this.archivedFolder.detail = {
        ...res.data,
        folders: res.data.results.map((f) => {
          const res = new Folder(f);
          return res;
        }),
      };
      this.archivedFolder.loading = false;
    },
    removeFilter(filter) {
      if (filter.key == "name") {
        this.queryString = "";
      }
      this.filters[filter.key] = Array.isArray(this.filters[filter.key])
        ? []
        : "";
      const tmp = this.advancedFilters.find((item) => item.key === filter.key);
      if (tmp) tmp.value = [];
      if (
        (this.$route.query.status || this.$route.query.workflow) &&
        ["status", "workflow"].includes(filter.key)
      ) {
        this.saveFilters(this.organizationId);
        this.$router.push({
          name: "folderList",
          params: { organizationId: this.organizationId },
        });
        return;
      }
      this.fetchFolders();
    },
    handleInputFilter() {
      if (this.searchTimeoutRef) clearTimeout(this.searchTimeoutRef);
      this.searchTimeoutRef = setTimeout(async () => {
        this.fetchFolders();
      }, 500);
    },
    handleChangeFilter(folderState) {
      switch (folderState) {
        case "OPEN":
          this.filters.activeFolderPage = this.filters.page;
          break;
        case "CLOSED":
          this.filters.archivedFolderPage = this.filters.page;
          break;
      }
      this.fetchFolders();
    },
    searchFolderWithQueryString() {
      if (this.searchTimeoutRef) clearTimeout(this.searchTimeoutRef);
      this.searchTimeoutRef = setTimeout(async () => {
        this.filters.page = 1;
        this.filters.activeFolderPage = 1;
        this.filters.archivedFolderPage = 1;
        this.filters.name = this.queryString;
        this.fetchFolders();
      }, 500);
    },
    changeTab(tab) {
      this.activeTabLabel = tab;
      switch (tab) {
        case "OPEN":
          this.filters.page = this.filters.activeFolderPage;
          break;
        case "CLOSED":
          this.filters.page = this.filters.archivedFolderPage;
          break;
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.header {
  display: flex;
  align-items: center;
  .header_left {
    width: 167px;
    flex: none;
  }
  .header_right {
    width: 250px;
    flex: none;
  }
  .header_center {
    width: calc(100% - 417px);
    overflow: hidden;
    display: flex;
    align-items: center;
  }
  .filters {
    display: flex;
    padding: 0 24px;
    align-items: center;
    .filter_icon {
      flex: none;
      background: #f4f7ff;
      border-radius: 9px;
      height: 36px;
      width: 36px;
      display: flex;
      justify-content: center;
      align-items: center;
      &::v-deep {
        .v-icon {
          height: 20px;
          width: 20px;
          color: #242533;
        }
      }
    }

    .filter_item {
      border-radius: 36px;
      background: #f4f7ff;
      padding: 8px 16px;
      display: flex;
      align-items: center;
      margin-left: 8px;
      .filter_item_icon {
        width: 18px;
        height: 18px;
        margin-right: 4px;
        display: flex;
        align-items: center;
        justify-content: center;
        &::v-deep {
          .v-icon {
            height: 18px;
            width: 18px;
            color: #707080;
          }
        }
      }
      .filter_item_label {
        color: #242533;
        font-family: "Inter";
        font-size: 12px;
        line-height: 20px;
        font-style: normal;
        font-weight: 500;
      }
      .close_icon {
        width: 18px;
        height: 18px;
        margin-left: 4px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        &::v-deep {
          .v-icon {
            height: 18px;
            width: 18px;
            color: #2536cc;
          }
        }
      }
    }
  }
}
.filter-container {
  display: block;
  margin-top: 16px;
  .filter-title {
    display: flex;
    align-items: center;
    color: #2536cc;
    font-size: 16px;
    cursor: pointer;
  }
  .filter-items {
    margin: 24px 0 24px 24px;
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    ::v-deep {
      .v-text-field__details {
        display: none;
      }
    }

    .filter-item {
      display: flex;
      gap: 8px;
      height: 40px;
      align-items: center;
      width: 48%;
      .filter-label-text {
        width: clamp(100px, 120px, 300px);
      }
      .input-field {
        max-width: 320px;
      }
    }
  }
}
.tab_items {
  overflow: visible;
}
.separator {
  width: 100%;
  border: 1px solid #2536cc;
}
</style>
